import React, { useRef, useEffect, useCallback, useMemo, useState } from "react";
import { EnergySavingsLeafOutlined } from "@mui/icons-material";
import { EditionBadge, getEditionBadge } from "../../utils/editionBadges";

interface EditionBadgeCanvasProps {
  /** 1-based edition number (1–12) */
  editionId: number;
  /** Whether this badge is in its active (minting/glowing) state */
  active?: boolean;
  /** Canvas pixel size (default 120) */
  size?: number;
  /** If provided, a Mint button is shown inside the tooltip */
  onMint?: () => void;
  /** Called when the tooltip opens or closes */
  onTooltipChange?: (open: boolean) => void;
}

// ─── Lighting ────────────────────────────────────────────────────────────────
const LX = -0.5,
  LY = -0.8,
  LZ = 0.6;
const LL = Math.sqrt(LX * LX + LY * LY + LZ * LZ);
const LIGHT = { x: LX / LL, y: LY / LL, z: LZ / LL };

/** Highest mint rate across all editions — drives the dynamic holo engine. */
const MAX_RATE = 27.3791;

// ─── Colour helpers ──────────────────────────────────────────────────────────
function hexToRgb(h: string): number[] {
  return [parseInt(h.slice(1, 3), 16), parseInt(h.slice(3, 5), 16), parseInt(h.slice(5, 7), 16)];
}

function applyLight(rgb: number[], f: number): number[] {
  return rgb.map((c) => Math.min(255, Math.max(0, Math.round(c * f))));
}

function hslToRgb(h: number, s: number, l: number): number[] {
  s /= 100;
  l /= 100;
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  let r = 0,
    g = 0,
    b = 0;
  if (h < 60) {
    r = c;
    g = x;
  } else if (h < 120) {
    r = x;
    g = c;
  } else if (h < 180) {
    g = c;
    b = x;
  } else if (h < 240) {
    g = x;
    b = c;
  } else if (h < 300) {
    r = x;
    b = c;
  } else {
    r = c;
    b = x;
  }
  return [(r + m) * 255, (g + m) * 255, (b + m) * 255];
}

interface Vec3 {
  x: number;
  y: number;
  z: number;
}

function fullNormal(p0: Vec3, p1: Vec3, p2: Vec3): Vec3 {
  const abx = p1.x - p0.x,
    aby = p1.y - p0.y,
    abz = p1.z - p0.z;
  const acx = p2.x - p0.x,
    acy = p2.y - p0.y,
    acz = p2.z - p0.z;
  const nx = aby * acz - abz * acy,
    ny = abz * acx - abx * acz,
    nz = abx * acy - aby * acx;
  const len = Math.sqrt(nx * nx + ny * ny + nz * nz) || 1;
  return { x: nx / len, y: ny / len, z: nz / len };
}

// ─── Main draw routine ──────────────────────────────────────────────────────
function drawGem(canvas: HTMLCanvasElement, gem: EditionBadge, rotY: number, hueShift: number) {
  const W = canvas.width,
    H = canvas.height,
    cx = W / 2,
    cy = H / 2,
    R = W / 2 - 0.5;
  const ctx = canvas.getContext("2d");
  if (!ctx) return;

  ctx.clearRect(0, 0, W, H);
  ctx.save();

  // Clip to circle
  ctx.beginPath();
  ctx.arc(cx, cy, R, 0, Math.PI * 2);
  ctx.clip();

  const isEd1 = gem.edition === 1;

  // 1. Outer ring
  const idleRingColor = isEd1 ? "rgba(168, 85, 247, 0.15)" : "rgba(255, 255, 255, 0.05)";
  ctx.fillStyle = gem.active ? "#A855F7" : idleRingColor;
  ctx.beginPath();
  ctx.arc(cx, cy, R, 0, Math.PI * 2);
  ctx.fill();

  // 2. Inner void — deep gem-specific flood when active, ultra-dark void when idle
  const idleInner = isEd1 ? "#0a0512" : "#060708";
  ctx.fillStyle = gem.active ? gem.activeBg : idleInner;
  ctx.beginPath();
  ctx.arc(cx, cy, R * 0.92, 0, Math.PI * 2);
  ctx.fill();

  // 3. Base plate
  ctx.fillStyle = gem.base;
  ctx.beginPath();
  ctx.arc(cx, cy, R * 0.84, 0, Math.PI * 2);
  ctx.fill();

  // ── Geometry ──
  const gr = 0.44,
    dh = 0.58,
    FOV = 2.6,
    sc = R * 0.78;
  const G = [0, 1.57, 3.14, 4.71].map((a) => ({
    x: Math.cos(a + rotY) * gr,
    y: 0,
    z: Math.sin(a + rotY) * gr,
  }));
  const TOP: Vec3 = { x: 0, y: -dh, z: 0 };
  const BOT: Vec3 = { x: 0, y: dh, z: 0 };

  function proj(p: Vec3) {
    const z = p.z + FOV;
    return { x: cx + (p.x / z) * FOV * sc, y: cy + (p.y / z) * FOV * sc };
  }
  const GP = G.map(proj),
    TOPP = proj(TOP),
    BOTP = proj(BOT);

  interface Face {
    v2: { x: number; y: number }[];
    v3: Vec3[];
    light: number;
    crown: boolean;
    idx: number;
    z: number;
  }

  const faces: Face[] = [];
  for (let i = 0; i < 4; i++) {
    const j = (i + 1) % 4;
    const cn = fullNormal(TOP, G[i], G[j]);
    const cl = Math.max(0, cn.x * LIGHT.x + cn.y * LIGHT.y + cn.z * LIGHT.z);
    faces.push({
      v2: [TOPP, GP[i], GP[j]],
      v3: [TOP, G[i], G[j]],
      light: cl,
      crown: true,
      idx: i,
      z: (G[i].z + G[j].z) / 2,
    });
    const pn = fullNormal(BOT, G[j], G[i]);
    const pl = Math.max(0, pn.x * LIGHT.x + pn.y * LIGHT.y + pn.z * LIGHT.z);
    faces.push({
      v2: [BOTP, GP[j], GP[i]],
      v3: [BOT, G[j], G[i]],
      light: pl,
      crown: false,
      idx: i,
      z: (G[i].z + G[j].z) / 2,
    });
  }
  // Sort back-to-front
  faces.sort((a, b) => a.z - b.z);

  faces.forEach((f) => {
    if (fullNormal(f.v3[0], f.v3[1], f.v3[2]).z <= 0) return; // back-face cull

    let rgb: number[];
    if (gem.prismatic) {
      const foilHues = [45, 150, 270, 330];
      const baseHue = (foilHues[f.idx] + hueShift) % 360;
      const baseLightness = f.crown ? (f.idx % 2 ? 85 : 65) : f.idx % 2 ? 45 : 30;
      rgb = hslToRgb(baseHue, 95, baseLightness);
    } else {
      const col = f.crown ? (f.idx % 2 ? gem.f1 : gem.f2) : f.idx % 2 ? gem.f3 : gem.f4;
      rgb = hexToRgb(col);
    }

    const lit = applyLight(rgb, 0.4 + f.light * 1.2);
    ctx.fillStyle = `rgb(${lit[0]},${lit[1]},${lit[2]})`;
    ctx.beginPath();
    ctx.moveTo(f.v2[0].x, f.v2[0].y);
    f.v2.forEach((p) => ctx.lineTo(p.x, p.y));
    ctx.fill();
  });

  ctx.restore();
}

// ─── Component ───────────────────────────────────────────────────────────────
const EditionBadgeCanvas: React.FC<EditionBadgeCanvasProps> = ({ editionId, active = false, size = 120, onMint, onTooltipChange }) => {
  const badge = useMemo(() => getEditionBadge(editionId, active), [editionId, active]);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const animRef = useRef<number>(0);
  const t0Ref = useRef<number | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  const openTooltip = useCallback((next: boolean) => {
    setIsOpen(next);
    onTooltipChange?.(next);
  }, [onTooltipChange]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        openTooltip(false);
      }
    };
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, openTooltip]);

  if (!badge) return null;

  const phaseOffset = ((badge.edition - 1) / 12) * Math.PI * 2;

  const loop = useCallback(
    (ts: number) => {
      if (!t0Ref.current) t0Ref.current = ts;
      const elapsed = (ts - t0Ref.current) / 1000;
      const canvas = canvasRef.current;
      if (!canvas) return;

      // Match the HTML: active rotation = pulse * 1800ms, idle = 12000ms
      const rotPeriod = badge.active ? badge.pulse * 1800 : 12000;
      const rotY = (((elapsed * 1000) / rotPeriod) * Math.PI * 2 + phaseOffset) % (Math.PI * 2);

      drawGem(canvas, badge, rotY, elapsed * 15);
      animRef.current = requestAnimationFrame(loop);
    },
    [badge, phaseOffset]
  );

  useEffect(() => {
    animRef.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(animRef.current);
  }, [loop]);

  // ── Glow layer — radial pulse synced to the gem's mint rate ──
  const glowBg = badge.active ? `radial-gradient(circle, rgba(${badge.glow}, 0.50) 0%, transparent 75%)` : "none";
  const glowAnimation = badge.active ? `glow-pulse ${badge.pulse}s ease-in-out infinite` : "none";

  // ── Dynamic holographic foil engine (matches the HTML) ──
  const intensity = Math.pow(badge.rate / MAX_RATE, 0.5);
  const activeHoloAlpha = 0.15 + intensity * 0.35;
  const activeHoloSpeed = 12.0 - intensity * 7.0;
  const idleHoloAlpha = 0.05 + intensity * 0.08;
  const idleHoloSpeed = 20.0 - intensity * 5.0;

  const holoAlpha = badge.active ? activeHoloAlpha : idleHoloAlpha;
  const holoSpeed = badge.active ? activeHoloSpeed : idleHoloSpeed;

  return (
    <div
      ref={containerRef}
      onClick={(e) => {
        e.stopPropagation();
        openTooltip(!isOpen);
      }}
      style={{
        display: "inline-flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 10,
        cursor: "pointer",
        position: "relative",
      }}
    >
      {/* Tooltip */}
      <div
        className={`edition-tooltip ${isOpen ? "visible" : ""}`}
        style={{
          position: "absolute",
          left: "calc(100% + 18px)",
          top: "50%",
          transform: isOpen ? "translateY(-50%) translateX(0)" : "translateY(-50%) translateX(-12px)",
          background: "rgba(10, 8, 20, 0.96)",
          backdropFilter: "blur(14px)",
          border: "1px solid rgba(188, 146, 248, 0.3)",
          borderRadius: "12px",
          padding: "12px 20px",
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          whiteSpace: "nowrap",
          zIndex: 1000,
          boxShadow: "0 15px 40px rgba(0, 0, 0, 0.6), 0 0 20px rgba(188, 146, 248, 0.12)",
          opacity: isOpen ? 1 : 0,
          pointerEvents: isOpen ? "auto" : "none",
          transition: "all 0.35s cubic-bezier(0.2, 0.8, 0.2, 1)",
        }}
      >
        {/* Close Button */}
        <div
          onClick={(e) => {
            e.stopPropagation();
            openTooltip(false);
          }}
          style={{
            position: "absolute",
            top: 8,
            right: 8,
            width: 18,
            height: 18,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            borderRadius: "50%",
            background: "rgba(255, 255, 255, 0.05)",
            color: "rgba(255, 255, 255, 0.4)",
            fontSize: 14,
            lineHeight: 1,
          }}
          className="tooltip-close-icon"
        >
          ×
        </div>

        <span
          style={{
            fontFamily: "'Inter', sans-serif",
            fontSize: 9,
            fontWeight: 600,
            color: "rgba(122, 117, 144, 0.8)",
            letterSpacing: "0.05em",
            textTransform: "uppercase",
            marginBottom: 2,
          }}
        >
          Edition ID : {editionId}
        </span>
        <span
          style={{
            fontFamily: "'Inter', sans-serif",
            fontSize: 14,
            fontWeight: 700,
            color: "#BC92F8",
            display: "flex",
            alignItems: "baseline",
            gap: 5,
          }}
        >
          {badge.rate}
          <span
            style={{
              fontSize: 9,
              fontWeight: 500,
              color: "rgba(188, 146, 248, 0.5)",
            }}
          >
            TR3/day
          </span>
        </span>

        {onMint && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              openTooltip(false);
              onMint();
            }}
            style={{
              marginTop: 10,
              width: "100%",
              background: "#BC92F8",
              border: "none",
              borderRadius: 8,
              padding: "6px 14px",
              cursor: "pointer",
              fontFamily: "'Inter', sans-serif",
              fontSize: 12,
              fontWeight: 600,
              color: "#000",
              letterSpacing: "0.02em",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 6,
            }}
          >
            <EnergySavingsLeafOutlined style={{ fontSize: 14, flexShrink: 0 }} />
            Mint
          </button>
        )}

        {/* Arrow */}
        <div
          style={{
            position: "absolute",
            right: "100%",
            top: "50%",
            transform: "translateY(-50%)",
            width: 0,
            height: 0,
            borderTop: "7px solid transparent",
            borderBottom: "7px solid transparent",
            borderRight: "7px solid rgba(10, 8, 20, 0.96)",
          }}
        />
      </div>

      {/* Glow + ring wrapper */}
      <div
        style={{
          position: "relative",
          width: size,
          height: size,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {/* Glow layer */}
        <div
          style={{
            position: "absolute",
            inset: -18,
            borderRadius: "50%",
            background: glowBg,
            animation: glowAnimation,
            pointerEvents: "none",
          }}
        />
        {/* Canvas + holographic foil overlay */}
        <div
          style={{
            position: "relative",
            zIndex: 2,
            width: size,
            height: size,
            borderRadius: "50%",
            clipPath: "circle(50% at 50% 50%)",
            WebkitClipPath: "circle(50% at 50% 50%)",
          }}
        >
          <canvas
            ref={canvasRef}
            width={size}
            height={size}
            style={{
              display: "block",
              borderRadius: "50%",
              width: size,
              height: size,
            }}
          />
          {/* Holographic foil — full-spectrum color-dodge shimmer */}
          <div
            className="holo-overlay"
            style={{
              position: "absolute",
              inset: -10,
              pointerEvents: "none",
              mixBlendMode: "color-dodge",
              opacity: holoAlpha,
              backgroundImage:
                "linear-gradient(110deg, transparent 5%, rgba(255, 30, 150, 0.7) 25%, rgba(30, 180, 255, 0.7) 45%, rgba(100, 255, 100, 0.6) 65%, rgba(255, 210, 30, 0.7) 85%, transparent 95%)",
              backgroundSize: "300% 300%",
              filter: "blur(5px)",
              animation: `beeple-shimmer ${holoSpeed}s infinite linear`,
            }}
          />
        </div>
      </div>

      {/* Inject keyframes once */}
      <style>{`
        @keyframes glow-pulse {
          0%, 100% { opacity: 0.25; transform: scale(0.92); }
          50%      { opacity: 0.80; transform: scale(1.08); }
        }
        @keyframes beeple-shimmer {
          0%   { background-position: 150% 150%; }
          100% { background-position: -50% -50%; }
        }
        @media (hover: hover) {
          .tooltip-close-icon {
            display: none !important;
          }
        }
        .tooltip-close-icon:hover {
          background: rgba(255, 255, 255, 0.1) !important;
          color: rgba(255, 255, 255, 0.8) !important;
        }
      `}</style>
    </div>
  );
};

export default EditionBadgeCanvas;
